import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BaselineCanvas } from "@/components/BaselineCanvas";
import { GrowthCanvas } from "@/components/GrowthCanvas";
import { RingsCanvas } from "@/components/RingsCanvas";
import { RedactedCanvas } from "@/components/RedactedCanvas";

const THEMES = {
  dark: {
    bg: "#1E1A17", title: "#F7F4EF", text: "#CFC6BA", ui: "#B5AB9F", track: "#5A5048", bar: "#D4A373",
    overlay: "bg-[linear-gradient(to_top,#1E1A17_10%,rgba(30,26,23,0.55)_55%,rgba(30,26,23,0.1))] lg:bg-[linear-gradient(to_right,#1E1A17_15%,rgba(30,26,23,0.6)_45%,rgba(30,26,23,0))]",
  },
  light: {
    bg: "#F7F4EF", title: "var(--kh-text)", text: "var(--kh-text-muted)", ui: "var(--kh-text-muted)", track: "var(--kh-border)", bar: "var(--kh-accent)",
    overlay: "bg-[linear-gradient(to_top,#F7F4EF_12%,rgba(247,244,239,0.7)_55%,rgba(247,244,239,0.1))] lg:bg-[linear-gradient(to_right,#F7F4EF_15%,rgba(247,244,239,0.75)_45%,rgba(247,244,239,0))]",
  },
  sand: {
    bg: "#E6DFD2", title: "var(--kh-text)", text: "var(--kh-text-muted)", ui: "var(--kh-text-muted)", track: "#CFC5B5", bar: "var(--kh-accent)",
    overlay: "bg-[linear-gradient(to_top,#E6DFD2_12%,rgba(230,223,210,0.75)_55%,rgba(230,223,210,0.1))] lg:bg-[linear-gradient(to_right,#E6DFD2_15%,rgba(230,223,210,0.75)_45%,rgba(230,223,210,0))]",
  },
};

const SLIDES = [
  {
    id: "question",
    title: "Kheiron began with a clinical question nobody wanted to ask aloud.",
    text: "Why, after decades of refined trauma care, were so many patients still returning to a baseline they had already outgrown?",
    Background: BaselineCanvas,
    theme: THEMES.dark,
  },
  {
    id: "method",
    title: "Kheiron is a clinical method, not a modality label.",
    text: "Built from eighteen years of trauma caseload data, four doctoral theses and one simple refusal: to accept that recovery is the ceiling.",
    Background: GrowthCanvas,
    theme: THEMES.light,
  },
  {
    id: "story",
    title: "Trauma is not the end of a person's story.",
    text: "Kheiron trains qualified therapists in a method dedicated to Post-Traumatic Growth — and to pre-emptive intervention in the hours and days before PTSD takes hold.",
    Background: RingsCanvas,
    theme: THEMES.sand,
  },
  {
    id: "cases",
    standalone: true,
    title: "Client Stories",
    titleNote: "(Anonymised to protect privacy)",
    text: "All names, identifying characteristics, and specific details have been changed or combined to protect client confidentiality",
    italic: true,
    Background: RedactedCanvas,
    theme: THEMES.light,
  },
];

const ease = [0.2, 0.8, 0.2, 1];
const INTERVAL = 9000;

const Words = ({ text, start = 0 }) => text.split(" ").map((w, i) => (
  <span key={i} aria-hidden="true" className="inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em] mr-[0.24em]">
    <motion.span className="inline-block" initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.25 + (start + i) * 0.05, ease }}>{w}</motion.span>
  </span>
));

const SlideText = ({ slide, action }) => (
  <motion.div className="absolute inset-0 flex items-end lg:items-center pointer-events-none" exit={{ opacity: 0, transition: { duration: 0.5 } }}>
    <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pb-24 lg:pb-0">
      <h1 aria-label={[slide.title, slide.titleNote].filter(Boolean).join(" ")} className="text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl max-w-4xl" style={{ fontFamily: "var(--kh-font-heading)", color: slide.theme.title }} data-testid={`hero-slide-title-${slide.id}`}>
        <Words text={slide.title} />
        {slide.titleNote && (
          <span className="block mt-3 text-[1.7rem] sm:text-4xl lg:text-5xl" style={{ color: slide.theme.text }}>
            <Words text={slide.titleNote} start={slide.title.split(" ").length} />
          </span>
        )}
      </h1>
      <motion.p className={`mt-8 text-base sm:text-lg leading-relaxed max-w-xl ${slide.italic ? "italic" : ""}`} style={{ color: slide.theme.text }}
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1, ease }} data-testid={`hero-slide-text-${slide.id}`}>
        {slide.text}
      </motion.p>
      {action && (
        <motion.div className="mt-10 pointer-events-auto" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.4, ease }}>
          {action}
        </motion.div>
      )}
    </div>
  </motion.div>
);

const Arrow = ({ dir, onClick, theme }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} aria-label={dir === "prev" ? "Previous slide" : "Next slide"} data-testid={`hero-slide-${dir}`}
      className="w-9 h-9 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 transition-[opacity,transform] duration-300 hover:scale-105"
      style={{ border: `1px solid ${theme.track}`, color: theme.ui }}>
      <Icon size={16} strokeWidth={1.5} />
    </button>
  );
};

const Controls = ({ index, total, go, theme }) => (
  <div className="absolute bottom-8 left-0 right-0">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 flex items-center gap-4 text-xs tracking-[0.22em]" style={{ fontFamily: "var(--kh-font-mono)", color: theme.ui, transition: "color 1s" }}>
      <span data-testid="hero-slide-counter">{String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
      <div className="flex gap-2">
        {Array.from({ length: total }).map((_, i) => (
          <button key={i} onClick={() => go(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`hero-slide-dot-${i}`} className="py-3 group">
            <span className="block h-px w-10 relative overflow-hidden" style={{ background: theme.track }}>
              {i === index && (
                <motion.span key={`p-${index}`} className="absolute inset-0 origin-left" style={{ background: theme.bar }}
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: INTERVAL / 1000, ease: "linear" }} />
              )}
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-2 ml-auto">
        <Arrow dir="prev" theme={theme} onClick={() => go((index - 1 + total) % total)} />
        <Arrow dir="next" theme={theme} onClick={() => go((index + 1) % total)} />
      </div>
    </div>
  </div>
);

export const HeroSlider = ({ only, action, short }) => {
  const slides = only ? SLIDES.filter((s) => s.id === only) : SLIDES.filter((s) => !s.standalone);
  const [index, setIndex] = useState(0);
  const slide = slides[index];
  const Bg = slide.Background;

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), INTERVAL);
    return () => clearTimeout(t);
  }, [index, slides.length]);

  return (
    <section className={`relative overflow-hidden ${short ? "h-[68svh] min-h-[520px] max-h-[680px]" : "h-[calc(100svh-80px)] min-h-[600px]"}`} style={{ background: slide.theme.bg, transition: "background-color 1.2s" }} data-testid="hero-slider">
      <AnimatePresence initial={false}>
        <motion.div key={slide.id} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }}>
          <Bg />
          <div className={`absolute inset-0 pointer-events-none ${slide.theme.overlay}`} />
        </motion.div>
      </AnimatePresence>
      <AnimatePresence initial={false}>
        <SlideText key={slide.id} slide={slide} action={action} />
      </AnimatePresence>
      {slides.length > 1 && <Controls index={index} total={slides.length} go={setIndex} theme={slide.theme} />}
    </section>
  );
};
